import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  Grid2 as Grid,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PageHeader from '../components/common/PageHeader';
import PartnerSelector from '../components/common/PartnerSelector';
import LoadingScreen from '../components/common/LoadingScreen';
import { useAuth } from '../context/AuthContext';
import { partnerService } from '../services/partnerService';
import { DEVICE_TYPES, DEVICE_LABELS } from '../constants';
import { getErrorMessage } from '../utils/format';

export default function Devices() {
  const { user, isAdmin } = useAuth();
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState('');
  const [allowed, setAllowed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    async function init() {
      setLoading(true);
      try {
        if (isAdmin) {
          const res = await partnerService.list({ limit: 100 });
          const list = res.partners || [];
          setPartners(list);
          if (list[0]) setPartnerId(list[0].id);
        } else if (user?.partnerId) {
          setPartnerId(user.partnerId);
        }
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [isAdmin, user?.partnerId]);

  useEffect(() => {
    if (!partnerId) return;
    partnerService
      .getDevices(partnerId)
      .then(setAllowed)
      .catch(err => setError(getErrorMessage(err)));
  }, [partnerId]);

  const toggle = deviceType => {
    setAllowed(prev =>
      prev.includes(deviceType) ? prev.filter(d => d !== deviceType) : [...prev, deviceType],
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await partnerService.updateDevices(partnerId, allowed);
      setSuccess('Device permissions updated');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <>
      <PageHeader
        title="Devices"
        subtitle="Configure which medical devices a partner can access"
        action={
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {isAdmin && (
              <PartnerSelector partners={partners} value={partnerId} onChange={setPartnerId} />
            )}
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!partnerId || saving}
            >
              Save
            </Button>
          </Box>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Grid container spacing={1}>
            {DEVICE_TYPES.map(type => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={type}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allowed.includes(type)}
                      onChange={() => toggle(type)}
                      disabled={!isAdmin}
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight={600}>{DEVICE_LABELS[type]}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {type}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </>
  );
}
